import { musician } from '@/mocks/musician';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export default function About() {
  useScrollAnimation();

  return (
    <>
      <title>About Jackson Lacefield | Tenor, Worship Leader &amp; Multi-Instrumentalist</title>
      <meta name="description" content="Jackson Lacefield is a tenor and church musician studying Church Music at Baylor University. Gifted with perfect pitch, he plays vocals, violin, piano, guitar, bass, and drums." />
      <meta name="keywords" content="Jackson Lacefield, about, tenor, worship leader, Baylor University, church music, perfect pitch, multi-instrumentalist" />
      <link rel="canonical" href="https://www.jacksonlacefield.com/about" />
      <meta property="og:title" content="About Jackson Lacefield | Tenor, Worship Leader &amp; Multi-Instrumentalist" />
      <meta property="og:description" content="Learn about Jackson Lacefield — a tenor and worship leader rooted in faith, studying Church Music at Baylor University." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://www.jacksonlacefield.com/about" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content="About Jackson Lacefield | Tenor, Worship Leader & Multi-Instrumentalist" />
      <meta name="twitter:description" content="Learn about Jackson Lacefield — a tenor and worship leader rooted in faith, studying Church Music at Baylor University." />
    <main className="min-h-screen bg-background-50">
      {/* ── Page Header ── */}
      <section className="page-header gradient-section-orange film-grain">
        <div className="section-container text-center relative z-10">
          <span className="inline-block font-label text-xs tracking-widest uppercase text-accent-500 mb-4 scroll-animate">
            About
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground-900 mb-3 scroll-animate">
            Jackson Lacefield
          </h1>
          <p className="font-heading text-lg md:text-xl text-foreground-600 font-medium scroll-animate">
            {musician.subtitle}
          </p>
        </div>
      </section>

      {/* ── Bio Section ── */}
      <section className="py-12 md:py-20 gradient-section film-grain section-popout">
        <div className="section-container">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-14 items-start">
            <div className="w-full lg:w-5/12 scroll-animate-left">
              <div className="lg:sticky lg:top-24">
                <div className="relative rounded-2xl overflow-hidden aspect-[4/5] film-grain popout-card">
                  <img
                    src={musician.headshot}
                    alt="Jackson Lacefield"
                    className="w-full h-full object-cover object-top transition-transform duration-1000 hover:scale-[1.02]"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-foreground-950/50 to-transparent p-4 md:p-6">
                    <p className="font-heading text-lg text-white">{musician.name}</p>
                    <p className="font-label text-xs text-white/60">{musician.roleLine}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full lg:w-7/12 flex flex-col justify-center scroll-animate-right">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-5 leading-tight">
                Who I Am
              </h2>
              <p className="font-body text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
                {musician.about.bio}
              </p>
              <div className="font-body text-sm md:text-base text-foreground-600 leading-relaxed space-y-4">
                {musician.about.extended.split('\n\n').map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Faith ── */}
      <section className="py-14 md:py-24 section-grey film-grain">
        <div className="section-container">
          <div className="max-w-3xl mx-auto scroll-animate">
            <span className="inline-block font-label text-xs tracking-widest uppercase text-accent-500 mb-4">
              Faith
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-5 leading-tight">
              {musician.faith.title}
            </h2>
            <p className="font-body text-sm md:text-base text-foreground-600 dark:text-white leading-relaxed mb-7">
              {musician.faith.text}
            </p>
            <div className="popout-card border-l-4 border-accent-400 rounded-r-xl p-6 md:p-7">
              <p className="font-heading text-base md:text-lg italic text-foreground-700 dark:text-white mb-2 leading-relaxed">
                &ldquo;{musician.faith.scripture}&rdquo;
              </p>
              <p className="font-label text-xs text-accent-500 dark:text-accent-300">&mdash; {musician.faith.scriptureRef}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Music ── */}
      <section className="py-14 md:py-24 gradient-section-mid film-grain section-popout">
        <div className="section-container">
          <div className="max-w-3xl mx-auto scroll-animate">
            <span className="inline-block font-label text-xs tracking-widest uppercase text-accent-500 mb-4">
              Music
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-5 leading-tight">
              {musician.music.title}
            </h2>
            <p className="font-body text-sm md:text-base text-foreground-600 leading-relaxed">
              {musician.music.text}
            </p>
          </div>
        </div>
      </section>

      {/* ── Outdoors ── */}
      <section className="py-14 md:py-24 section-grey film-grain">
        <div className="section-container">
          <div className="max-w-3xl mx-auto scroll-animate">
            <span className="inline-block font-label text-xs tracking-widest uppercase text-accent-500 mb-4">
              Life
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-5 leading-tight">
              {musician.outdoors.title}
            </h2>
            <p className="font-body text-sm md:text-base text-foreground-600 dark:text-white leading-relaxed mb-7">
              {musician.outdoors.text}
            </p>
            <div className="popout-card border-l-4 border-accent-400 rounded-r-xl p-6 md:p-7">
              <p className="font-heading text-base md:text-lg italic text-foreground-700 dark:text-white mb-2 leading-relaxed">
                &ldquo;{musician.outdoors.scripture}&rdquo;
              </p>
              <p className="font-label text-xs text-accent-500 dark:text-accent-300">&mdash; {musician.outdoors.scriptureRef}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Pull Quote ── */}
      <section className="py-14 md:py-24 gradient-section-orange film-grain section-popout">
        <div className="section-container max-w-3xl mx-auto">
          <div className="text-center scroll-animate">
            <div className="orange-divider mb-8">
              <svg viewBox="0 0 24 24" fill="none" className="w-3 h-3 text-accent-400">
                <path d="M12 4 L12 20" stroke="currentColor" strokeWidth="1" />
                <path d="M7 9 L17 9" stroke="currentColor" strokeWidth="1" />
              </svg>
            </div>
            <blockquote className="font-heading text-xl md:text-2xl italic text-foreground-700 leading-relaxed mb-4">
              &ldquo;{musician.about.pullQuote}&rdquo;
            </blockquote>
            <p className="font-label text-sm text-accent-500">&mdash; Jackson Lacefield</p>
          </div>
        </div>
      </section>
    </main>
    </>
  );
}